import React, { useEffect, useRef, useState } from "react";
import { IMessage, useChatState } from "../../state/ChatState";
import { useScript } from "../../../hooks/useScript";
import { applyConfiguration } from "../../helpers/applyConfiguration";
import { MicrophoneActions } from "../MicrophoneActions";
import { ChooseAgent } from "./ChooseAgent";
import { LoadingSpinner } from "../LoadingSpinner";

import { formatTime } from "../../helpers/helper";
import toast from "react-hot-toast";
import SymantoAvatar from "../../helpers/SymantoAvatar";
import UserAvatar from "../../helpers/UserAvatar";
import { useWindowDimensions } from "../../../hooks";
import CallEndedBanner from "../CallEndedBanner";

const scriptLocationForAudio = "/scripts/testAgentScriptVoice.js";

export const DigitalFriendWS = () => {
	const applicationState = useChatState();
	const loaded = useScript({ src: scriptLocationForAudio });
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	const [initApproved, setInitApproved] = useState(false);
	const [isInitializing, setInitializing] = useState(false);
	const [languageIndex, setLanguageIndex] = useState(0);
	const [callDuration, setCallDuration] = useState(0);
	const [lastCallDuration, setLastCallDuration] = useState<number | null>(null);
	const callDurationRef = useRef(callDuration);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const showImages = applicationState.baseConfig?.SHOW_IMAGES != "false";

	const isVoiceChat = applicationState.baseConfig?.IS_VOICE_CHAT == "true";
	const inCallLabels = applicationState.baseConfig?.IN_CALL_LABELS?.split("|");
	const connectingMessages =
		applicationState.baseConfig?.CONNECTING_MESSAGES?.split("|");
	const productChatLogoUrl =
		applicationState.baseConfig?.PRODUCT_LOGO_URL || "/symanto_logo_new.webp";
	const headerLogoUrl =
		applicationState.baseConfig?.HEADER_LOGO_URL || productChatLogoUrl;
	const primaryColor = applicationState.baseConfig?.PRIMARY_COLOR ?? "#1A0A91";
	const { width: screenWidth } = useWindowDimensions();
	const isCompact = screenWidth < 800;
	const [startedConversation, setStartedConversation] = useState(false);
	const queryParams = new URLSearchParams(location.search);
	const sourceName = queryParams.get("sourceName") || "";
	const [promptId, setPromptId] = useState<string | undefined>(undefined);
	const [agentId, setAgentId] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (callDuration) callDurationRef.current = callDuration;
	}, [callDuration]);

	useEffect(() => {
		if (lastCallDuration === null) return;
		const t = setTimeout(() => setLastCallDuration(null), 3000);
		return () => clearTimeout(t);
	}, [lastCallDuration]);

	applyConfiguration(
		String(applicationState.baseConfig?.PRIMARY_COLOR),
		String(applicationState.baseConfig?.SECONDARY_COLOR),
		String(applicationState.baseConfig?.TITLE),
		applicationState.baseConfig?.BACKGROUND_COLOR,
	);

	const startCall = (initPromptId: any, initAgentId: any) => {
		setInitApproved(true);
		setLastCallDuration(null);
		const clean = (v: any) =>
			v !== undefined && v !== null && v !== "undefined" && v !== ""
				? String(v)
				: undefined;
		const _agentId =
			clean(initPromptId) ??
			clean(initAgentId) ??
			clean(promptId) ??
			clean(agentId);
		if (!_agentId) {
			console.error("startCall: no valid agent id available");
			return;
		}
		window.symantoAgentTester.startConversation(
			_agentId,
			applicationState.baseConfig!.API_KEY,
			applicationState.baseConfig!.BASE_API_URL,
			false,
			undefined,
			sourceName || window.location.host,
			primaryColor,
		);

		window.symantoAgentTester.messageChannel.port2.onmessage = (event) => {
			const { id, role, isComplete, content } = event.data;

			const _msg: IMessage = {
				id: id,
				conversationId: id,
				messageAuthor: role == "2" ? 0 : 1,
				text: content,
				finalMessage: isComplete,
				date: new Date().toISOString(),
			};

			applicationState.updateMessage(_msg, true);
		};

		applicationState.setIsMicActive(true);
		setInitializing(false);
		setStartedConversation(true);
		startDuration();

		window.symantoAgentTester.eventChannel.port2.onmessage = handleEventMessage;
	};

	const handleEventMessage = (event: { data: { type: any; message: any } }) => {
		const { type, message } = event.data;
		if (type == "success") {
			toast.dismiss();
			toast.success(message);
		}
		if (type == "error") {
			toast.error(message);
		}
	};

	const stopCall = () => {
		const finalDuration = callDurationRef.current;
		try {
			window.symantoAgentTester?.stopConversation?.();
		} catch (e) {
			console.error(e);
		}
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		applicationState.setIsMicActive(false);
		setInitApproved(false);
		setStartedConversation(false);
		setInitializing(false);
		setCallDuration(0);
		callDurationRef.current = 0;
		setLastCallDuration(finalDuration);
	};

	const pauseMicrophone = () => {
		window.symantoAgentTester.pauseMic();
		applicationState.setIsMicActive(false);
	};

	const unpauseMicrophone = () => {
		window.symantoAgentTester.unpauseMic();
		applicationState.setIsMicActive(true);
	};

	const initChat = (
		langIndex: number,
		initAgentId: string,
		initPromptId?: string,
	) => {
		setPromptId(initPromptId);
		setAgentId(initAgentId);
		setInitApproved(true);
		setInitializing(true);
		setLanguageIndex(langIndex);

		startCall(initPromptId, initAgentId);
	};

	const startDuration = () => {
		intervalRef.current = setInterval(() => {
			setCallDuration((prev) => prev + 1);
		}, 1000);
	};

	useEffect(() => {
		if (loaded && initApproved && canvasRef.current) {
			window.symantoAgentTester?.setupCanvas(primaryColor);
		}
	}, [loaded, initApproved, primaryColor]);

	if (loaded === false) {
		return (
			<div className="min-h-[100vh] max-h-[100vh] flex overflow-y-auto s_gradient_symanto">
				<div className="flex flex-1 items-center justify-center">
					<LoadingSpinner />
				</div>
			</div>
		);
	}

	// showHeaderLogo is currently disabled read more here: #41316
	// const showHeaderLogo = !!headerLogoUrl && showImages && !initApproved;
	const showHeaderLogo = false;
	const showBottomLogo = showImages && lastCallDuration === null;

	return (
		<div className="min-h-[100vh] max-h-[100vh] flex overflow-y-auto s_gradient_symanto">
			<div className="flex flex-1 flex-col items-center p-4 overflow-y-auto">
				<div
					data-id="app_header"
					className="w-full flex justify-center pt-6 md:pt-10"
				>
					{showHeaderLogo && (
						<img
							src={headerLogoUrl}
							className="h-[54px] max-h-[54px] md:h-[68px] md:max-h-[68px] object-contain"
							alt="Logo"
						/>
					)}
				</div>

				<div
					data-id="app_middle"
					className="flex flex-1 flex-col items-center justify-center w-full"
				>
					<div data-id="app_microphone_actions">
						{isVoiceChat && initApproved && (
							<div className="absolute bottom-0 left-0 mb-0 ml-3 z-10">
								<MicrophoneActions
									isActive={applicationState.isMicActive}
									onPlay={() => unpauseMicrophone()}
									onStop={() => pauseMicrophone()}
								/>
							</div>
						)}
					</div>

					{initApproved && (
						<div className="w-full flex flex-col flex-1 overflow-y-auto">
							{isInitializing && (
								<div className="flex w-full mt-1 md:mt-4 gap-3 items-center justify-center">
									<p className="text-sm md:text-base">
										{connectingMessages?.[languageIndex]}
									</p>
								</div>
							)}

							{!isInitializing && (
								<>
									<div className="flex items-center justify-center mb-4 md:mb-6">
										{isVoiceChat && (
											<p className="text-sm md:text-base">
												{inCallLabels?.[languageIndex]?.replace(
													"{{time}}",
													formatTime(callDuration),
												)}
												.
											</p>
										)}
									</div>
									<div className="flex flex-1 flex-col items-center justify-center gap-6">
										<div className="flex items-center justify-center gap-4 w-full">
											<SymantoAvatar size={40} />
											<canvas
												ref={canvasRef}
												id="waveCallCanvas"
												style={
													isCompact
														? {
																width: "242px",
																height: "400px",
															}
														: { width: "600px", height: "300px" }
												}
											/>
											<UserAvatar color={primaryColor || "#1A0A91"} size={40} />
										</div>

										{!startedConversation ? (
											<button
												onClick={() => startCall(undefined, undefined)}
												className="w-14 h-14 rounded-full border border-gray-300 bg-[#f1f2f4] flex items-center justify-center shadow-sm hover:shadow-md transition"
												aria-label="Start"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 24 24"
													className="w-8 h-8 text-black"
												>
													<circle
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="2"
														fill="none"
													/>
													<polygon
														points="9,7 17,12 9,17"
														fill="currentColor"
													/>
												</svg>
											</button>
										) : (
											<button
												onClick={stopCall}
												className="w-14 h-14 rounded-full bg-black flex items-center justify-center shadow-md hover:shadow-lg transition"
												aria-label="End Call"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 24 24"
													fill="none"
													stroke="white"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
													className="w-6 h-6"
												>
													<path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
													<line x1="23" y1="1" x2="1" y2="23" />
												</svg>
											</button>
										)}
									</div>
								</>
							)}
						</div>
					)}

					{!initApproved && <ChooseAgent selectedAgent={initChat} />}
				</div>

				<div
					data-id="app_bottom"
					className="w-full flex flex-col items-center pb-6 md:pb-10 gap-4"
				>
					{lastCallDuration !== null && (
						<CallEndedBanner durationSeconds={lastCallDuration} />
					)}
					{showBottomLogo && (
						<img
							src="/powered-by-symanto-logo.svg"
							className="h-[32px] max-h-[32px] md:h-[40px] md:max-h-[40px] object-contain opacity-90"
							alt="Logo"
						/>
					)}
				</div>
			</div>
		</div>
	);
};
